import React, { useEffect, useState, useMemo, useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import InnerLayout from "../../components/layouts/inner-layout";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import errorToast from "../../components/helper-components/error-toast";
import { getApprovalListAction } from "../../redux/actions/approval-work-flow-action";
import { DataContext } from "../../components/layouts/main-layout";
import { FileText, TrendingUp, Clock } from "lucide-react";
import DynamicTransactionTable from "../../components/tables/dynamic-transaction-table";
import TransSummaryComp from "../../components/helper-components/trans-summary-comp";

const ApprovalList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissionFn } = useContext(DataContext);

  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dataCount, setDataCount] = useState(0);
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    selectColumn: "",
    inputValue: "",
  });

  // ===== Fetch approval list =====
  useEffect(() => {
    setLoading(true);
    dispatch(
      getApprovalListAction(
        {
          pageNumber:currentPage,
          pageSize:pageSize,
          selectColumn: filterData.selectColumn,
          inputValue:filterData.inputValue
        },
        (data) => {
          setApiData(Array.isArray(data?.data) ? data.data : []);
          setLoading(false);
        },
        (err) => {
          setLoading(false);
          errorToast(err?.message || "Failed to load approvals");
        }
      )
    );
  }, [dispatch, reload, currentPage, pageSize,filterData]);


  // ===== Summary Cards =====
  const list = Array.isArray(apiData) ? apiData : [];

const totalRequests = list.length;
const approvedCount = list.filter(
  (r) => (r?.status || "").toLowerCase() === "completed"
).length;
const pendingCount = list.filter(
  (r) => (r?.status || "").toLowerCase() === "pending"
).length;
const approvalRate =
  totalRequests > 0 ? Math.round((approvedCount / totalRequests) * 100) : 0;

  const totalDays = list
    .filter((r) => (r.status || "").toLowerCase() === "completed")
    .reduce((acc, req) => {
      const created = new Date(req.created_at);
      const completed = new Date(req.updated_at || req.created_at);
      return (
        acc +
        Math.max(
          0,
          Math.floor((completed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
        )
      );
    }, 0);

  const avgApprovalTime =
    approvedCount > 0 ? (totalDays / approvedCount).toFixed(1) : "0";

  const summaryArray = (
    <div className="px-[24px] pt-[16px] flex flex-wrap gap-5">
      {[
        {
          conClass:
            "bg-[#e8f0fe] rounded-[12px] h-[5.3rem] flex-1 min-w-[240px] p-3 flex justify-between",
          label: "Total Requests",
          value: totalRequests,
          desc: `+2 this week`,
          descClass: "text-xs font-medium text-[#4285f4]",
          icon: <FileText className="text-[#4285f4]" />,
        },
        {
          conClass:
            "bg-[#dcfce7] rounded-[12px] h-[5.3rem] flex-1 min-w-[240px] p-3 flex justify-between",
          label: "Approved / Pending",
          value: `${approvedCount} / ${pendingCount}`,
          desc: "Status Overview",
          descClass: "text-xs font-medium text-[#16a34a]",
          icon: <TrendingUp className="text-[#16a34a]" />,
        },
        {
          conClass:
            "bg-[#f3e8ff] rounded-[12px] h-[5.3rem] flex-1 min-w-[240px] p-3 flex justify-between",
          label: "Approval Rate",
          value: `${approvalRate}%`,
          desc: `${approvedCount} of ${totalRequests} approved`,
          descClass: "text-xs font-medium text-[#9333ea]",
          icon: <TrendingUp className="text-[#9333ea]" />,
        },
        {
          conClass:
            "bg-[#ffedd5] rounded-[12px] h-[5.3rem] flex-1 min-w-[240px] p-3 flex justify-between",
          label: "Avg. Approval Time",
          value: avgApprovalTime,
          desc: "Days to approve",
          descClass: "text-xs font-medium text-[#ea580c]",
          icon: <Clock className="text-[#ea580c]" />,
        },
      ].map((sum, i) => (
        <TransSummaryComp key={i} sum={sum} />
      ))}
    </div>
  );

  // ===== Table Headings =====
  const tableHeading = [
    { label: "REQUEST ID", value: "request_id", minWidth: 120, column: true, display: true, enabled: true },
    { label: "REQUESTER", value: "requester", minWidth: 150, column: true, display: true, enabled: true },
    { label: "REQUEST DATE", value: "created_at", minWidth: 150, column: false, display: true, enabled: true },
    { label: "TYPE", value: "document_type", minWidth: 140 , column: true, display: true, enabled: true},
    { label: "TITLE", value: "title", minWidth: 200, column: true, display: true, enabled: true },
    { label: "STATUS", value: "status", minWidth: 130, column: true, display: true, enabled: true },
    { label: "ACTIONS", value: "actions", minWidth: 100, column: false, display: true, enabled: true },
  ];

  // ===== Client-side search =====
  const handleFilter = () => {
    const text = (filterData.inputValue || "").toLowerCase().trim();
    const col = filterData.selectColumn;
    if (!text || !col) return list;

    return list.filter((r) => {
      const doc = r.document_details || {};
      const fields = {
        request_id: String(r.request_id || ""),
        requester: doc.cmr_name || "",
        created_at: r.created_at || "",
        document_type: doc.document_type || "",
        title: `${(doc.document_type || "")
          .replace(/_/g, " ")
          .toUpperCase()} - ${doc.cmr_name || ""}`,
        status: r.status || "",
      };
      return fields[col]?.toLowerCase().includes(text);
    });
  };

  const tableBodyData = useMemo(() => {
    const filtered = handleFilter();
    return (filtered || list).map((r) => {
      const doc = r.document_details || {};
      const title = `${(doc.document_type || "")
        .replace(/_/g, " ")
        .toUpperCase()} - ${doc.cmr_name || ""}`;
      const formatDate = (d) =>
        d
          ? new Date(d).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "-";

      return {
        id: r.request_id,
        request_id: (
          <span
            className="text-[#4285f4] cursor-pointer hover:underline"
            onClick={() =>
              navigate(`/approval-work-flow/acceptance/${r.request_id}`)
            }
          >
            #{r.request_id}
          </span>
        ),
        requester: doc.cmr_name || "-",
        created_at: formatDate(r.created_at),
        document_type: (
          <span className="bg-[#e8f0fe] text-[#4285f4] px-2 py-1 rounded-md text-xs">
            {doc.document_type || "-"}
          </span>
        ),
        title: title,
        status: (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              (r.status || "").toLowerCase() === "completed"
                ? "bg-[#e5fbe9] text-[#5ca55e]"
                : (r.status || "").toLowerCase() === "pending"
                ? "bg-[#FFEDD4] text-[#9F2D00]"
                : "bg-gray-50 text-gray-600"
            }`}
          >
            {r.status}
          </span>
        ),
        actions: (
          <button
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            onClick={() =>
              navigate(`/approval-work-flow/acceptance/${r.request_id}`)
            }
          >
            <Clock className="w-4 h-4" />
          </button>
        ),
      };
    });
  }, [apiData, filterData, navigate]);

  const paginationProps = {
    pageSize: pageSize,
    setPageSize: setPageSize,
    dataCount: dataCount,
    setCurrentPage: setCurrentPage,
    currentPage: currentPage,
  };

  return (
    <InnerLayout title="Approval Request List" backBtn={true}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <DynamicTransactionTable
          transaction="Approval Request"
          transaction_type="Approval"
          tableHeading={tableHeading}
          is_checkbox_req={false}
          tableBodyData={tableBodyData}
          reload={reload}
          setReload={setReload}
          setLoading={setLoading}
          label="Requests"
          placeholder="Search requests..."
          summaryArray={summaryArray}
          paginationProps={paginationProps}
          onclkFn={() => navigate(`/approval-work-flow`)}
          permissionFn={permissionFn}
          module="approval"
          list={true}
          showFilter={true}
          filterSec={true}
          filterFn={handleFilter}
          setFilterData={setFilterData}
          filterData={filterData}
          date={false}
          path="approval-work-flow"
          invoiceItems={[]}
          setInvoiceItems={() => {}}
          orders={[]}
          setOrders={() => {}}
          showFilterArrows={false}
          onClickItemFn={(request_id) => navigate(`/approval-work-flow/acceptance/${request_id}`)}
          canCreateNew={true}
          filter={false}
        />
      )}
    </InnerLayout>
  );
};
export default ApprovalList;
